import { fastEqual } from '@flowus/common/utils/tools';
import { CommandType } from '@next-space/fe-api-idl';
import type { FC, MouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar } from 'src/common/components/avatar';
import { Divider } from 'src/common/components/divider';
import { Icon } from 'src/common/components/icon';
import { NextMenuItem } from 'src/common/components/next-menu-item';
import { useCloseModal, useIsExistModalId, useOpenModal } from 'src/common/components/next-modal';
import { SortableList } from 'src/common/components/sortable-list';
import { Tooltip } from 'src/common/components/tooltip';
import { request } from 'src/common/request';
import { DesktopComponents } from 'src/components/desktop';
import { IconUiSizeBox } from 'src/components/icon-ui-size-box';
import { usePullNotifications } from 'src/hooks/notifications/use-pull-notifications';
import { fetchSpaces, jumpAfterFetchSpaces, useAllSpaces, useCurrentSpace } from 'src/hooks/space';
import { useCurrentUser, useCurrentUserId } from 'src/hooks/user';
import { switchSpaceViewIdBySpaceId } from 'src/hooks/user/switch-space-view-id';
import { useFetchMe } from 'src/hooks/user/use-fetch-me';
import { Modals } from 'src/modals';
import { invalidateNotifications, useNotificationsMagic } from 'src/services/app';
import { useIsFullscreen } from 'src/services/desktop';
import { useGetSyncService } from 'src/services/sync';
import { ViewPath } from 'src/utils';
import { removeDuplicateWith } from 'src/utils/array-util';
import { bizTracker } from 'src/utils/biz-tracker';
import { getSpacePlanTypeName } from 'src/utils/block-utils';
import { isMacElectron } from 'src/utils/electron';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { v4 as uuidV4 } from 'uuid';
import { useUnreadState } from './message-panel/shared';
import { TocCollapseButton } from './toc-trigger';

// #region 头部区
export const AsideHeader: FC = () => {
  const currentSpace = useCurrentSpace();
  const { title, icon, backgroundColor } = currentSpace;
  const openModal = useOpenModal();
  const spaces = useAllSpaces();
  const notificationMagic = useNotificationsMagic();
  const isFullscreen = useIsFullscreen();
  const unread = useUnreadState();
  const otherSpacesHasUnread = useMemo(() => {
    const otherSpaces = spaces.filter((it) => it.uuid !== currentSpace.uuid);
    return otherSpaces.some((it) => {
      const ur = unread[it.uuid];
      if (!ur) return false;
      return ur.inbox + ur.mention > 0;
    });
  }, [unread, spaces, currentSpace.uuid]);
  const pullNotifications = usePullNotifications();

  const sync = useGetSyncService();
  const userId = useCurrentUserId();
  const isNotificationsPopupOpen = useIsExistModalId(Modals.MESSAGE_NOTIFICATION);
  useEffect(() => {
    return sync.subscribeNotifications(() => {
      if (!isNotificationsPopupOpen) {
        pullNotifications();
      }
      invalidateNotifications();
    });
  }, [sync, pullNotifications, userId, isNotificationsPopupOpen]);

  useEffect(() => {
    setTimeout(() => {
      // 别一上来就发请求，delay一下
      pullNotifications();
    }, 1500);
  }, [notificationMagic, pullNotifications]);

  const openSwitchSpace = (event: MouseEvent) => {
    bizTracker.event('space_view_click', {});
    openModal.dropdown({
      modalId: Modals.CHECK_SPACE,
      popcorn: event.currentTarget,
      offset: [0, 3],
      content: () => <SwitchSpace />,
    });
  };

  const isDesktopMode = isMacElectron && !isFullscreen;

  return (
    <>
      <DesktopComponents.AsideHeaderPlaceholder />
      <div className={'transition-all relative p-2 flex flex-col justify-center flex-shrink-0'}>
        <div
          className={`flex items-center px-2 py-1.5 rounded animate-hover w-full animate-click`}
          onClick={openSwitchSpace}
        >
          <Tooltip
            popup="切换空间"
            className="flex items-center space-x-2  "
            style={{
              width: isDesktopMode ? '100%' : 'calc(100% - 30px)',
            }}
          >
            <div className="relative">
              <Avatar
                className="flex-shrink-0"
                icon={icon}
                name={title}
                iconSize={26}
                color={backgroundColor}
              />
              {otherSpacesHasUnread && (
                <div className="absolute -top-0.5 -right-0.5 rounded-full w-1.5 h-1.5 flex-shrink-0 bg-red" />
              )}
            </div>
            <div className="flex items-center text-ellipsis gap-2">
              <span className="text-ellipsis max-w-full text-t2-medium">{title}</span>
              <Icon name="IcArrowDown01" className="text-grey3" size="xxsmall" />
            </div>
          </Tooltip>
          <TocCollapseButton />
        </div>
      </div>
    </>
  );
};
// #endregion

// #region 切换space弹窗
const SwitchSpace: FC = () => {
  const closeModal = useCloseModal();
  const history = useHistory();
  const spaces = useAllSpaces();
  const currentSpace = useCurrentSpace();
  const currentUser = useCurrentUser();
  const [spaceList, setSpaceList] = useState(spaces.map((i) => ({ ...i, id: i.uuid })));
  const unread = useUnreadState();
  const fetchMe = useFetchMe();
  const spaceListRef = useRef(spaceList);

  const handleClick = ({ type, uuid }: { type: string; uuid?: string }) => {
    switch (type) {
      case 'space':
        if (uuid && uuid !== currentSpace.uuid) {
          bizTracker.event('space_change_click');
          switchSpaceViewIdBySpaceId(uuid, true);
          history.push(ViewPath.main);
        }
        break;
      case 'create':
        bizTracker.event('space_new');
        history.push(ViewPath.create);
        break;
      default:
        break;
    }
    closeModal(Modals.CHECK_SPACE);
  };

  const changeSpaceSort = async (sortSpaceList: typeof spaceList) => {
    setSpaceList(sortSpaceList);
    spaceListRef.current = sortSpaceList;
  };

  useEffect(() => {
    void (async () => {
      await fetchMe();
      await fetchSpaces(true);
      jumpAfterFetchSpaces();
    })();
  }, [fetchMe]);

  const saveSpaceViews = async () => {
    const spaceIds = spaces.map((i) => i.uuid);
    const newSpaceIds = spaceListRef.current.map((i) => i.uuid);
    // 无需更新
    if (fastEqual(spaceIds, newSpaceIds)) {
      return;
    }
    const res = await request.infra.getUserRoot(currentUser.uuid);
    const newSpaceViewsId: string[] = [];
    spaceListRef.current.forEach((item) => {
      const space = Object.values(res.spaceViews ?? {}).find(
        (spaceView) => spaceView.spaceId === item.id
      );
      if (space) newSpaceViewsId.push(space.uuid);
    });
    const spaceViews = Object.keys(res.spaceViews ?? {});
    const newIds = [...new Set([...newSpaceViewsId, ...spaceViews])];

    await request.editor.transactionRecords({
      requestId: uuidV4(),
      transactions: [
        {
          id: uuidV4(),
          spaceId: currentSpace.uuid,
          operations: [
            {
              id: currentUser.uuid,
              table: 'user',
              command: CommandType.UPDATE,
              path: ['spaceViews'],
              args: newIds as any,
            },
          ],
        },
      ],
    });
    await fetchMe();
    await fetchSpaces(true);
  };

  useEffect(() => {
    return () => {
      void saveSpaceViews();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSpaceList(removeDuplicateWith(spaces, 'uuid').map((i) => ({ ...i, id: i.uuid })));
  }, [spaces]);

  return (
    <div className="w-64 p-2 pb-1 flex flex-col next-modal text-t2 h-full max-h-[70vh]">
      <div className="flex-shrink-0 p-2 text-ellipsis h-9 text-grey3">{currentUser.nickname}</div>
      <div className="overflow-y-auto pb-1 select-none">
        <SortableList
          onChange={(items) => void changeSpaceSort(items)}
          items={spaceList}
          renderItemContent={({ item, renderDragHandle }) => (
            <NextMenuItem
              showPopup
              {...bindDataTestId(TestIds.asideSpaceItem)}
              key={item.uuid}
              text={item.title}
              className="h-[58px]"
              description={getSpacePlanTypeName(item.planType)}
              active={currentSpace.uuid === item.uuid}
              onClick={() => handleClick({ type: 'space', uuid: item.uuid })}
              addonBefore={
                <div className="flex items-center">
                  {renderDragHandle({
                    className:
                      'cursor-pointer flex items-center justify-center w-5 h-5 mr-1 text-grey4',
                  })}
                  <div className="relative">
                    <Avatar
                      className="w-[34px] h-[34px]"
                      name={item.title}
                      icon={item.icon}
                      color={item.backgroundColor}
                      iconSize={34}
                      style={{ fontSize: '20px' }}
                    />
                    {(unread[item.uuid]?.mention ?? 0) + (unread[item.uuid]?.inbox ?? 0) > 0 && (
                      <div className="absolute top-[-4px] right-[-4px] rounded-full ml-0.5 w-2.5 h-2.5 bg-red" />
                    )}
                  </div>
                </div>
              }
            />
          )}
        />
      </div>
      <Divider className="mx-2 pb-1" />
      <NextMenuItem
        className="flex-shrink-0"
        staticStyle
        text="创建新空间"
        {...bindDataTestId(TestIds.asideSpaceCreate)}
        addonBefore={
          <IconUiSizeBox size={26}>
            <Icon name="IcAdd" size="middle" />
          </IconUiSizeBox>
        }
        onClick={() => handleClick({ type: 'create' })}
      />
    </div>
  );
};
// #endregion
